import type { ImapSettings, SmtpSettings } from 'src/types/checkout';

import { z as zod } from 'zod';
import { useMemo } from 'react';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';

export type EditChannelSchemaType = {
  channelName: string;
  assignUsers: any;
  pin?: string;
  pinAcknowledge?: boolean;
  email?: string;
  smtpEnabled?: boolean;
  smtpAddress?: string;
  smtpPort?: string;
  smtpLogin?: string;
  smtpPassword?: string;
  smtpDomain?: string;
  imapEnabled?: boolean;
  imapAddress?: string;
  imapPort?: number;
  imapLogin?: string;
  imapPassword?: string;
  imapEnableSsl?: boolean;
  websiteDomin?: string;
  widgetColor?: string;
  welcomeTitle?: string;
  welcomeTagline?: string;
  srcNumber?: string;
  authToken?: string;
  accountSid?: string;
  lineChannelId?: string;
  lineChannelSecret?: string;
  telegramBotToken?: string;
};

const addRequiredError = (ctx: zod.RefinementCtx, path: string, message: string) => {
  ctx.addIssue({ code: zod.ZodIssueCode.custom, path: [path], message });
};

const validateRequired = (
  value: string | undefined,
  ctx: zod.RefinementCtx,
  path: string,
  message: string
) => {
  if (!value?.trim()) addRequiredError(ctx, path, message);
};

const validatePort = (port: number | undefined, ctx: zod.RefinementCtx, path: string) => {
  if (!port || port <= 0) addRequiredError(ctx, path, 'Port must be a positive number');
};

export const useChannelValidation = (selectedChannel: any, isFacebookConnected: boolean) => {
  const { t } = useTranslate('translation');

  const fldList = useMemo(() => {
    const base = [
      { name: 'channelName', required: true },
      { name: 'assignUsers', required: true },
    ];

    // WHAT & WHY: Added explicit field mappings for all 10 channel types (including VOICE, MICROSOFTTEAMS, MESSENGER, INSTAGRAM)
    // so that form validation works consistently across all channel additions and edits.
    const fieldMap: Record<string, any[]> = {
      EMAIL: [{ name: 'email', required: true }],
      WHATSAPP: [
        { name: 'pin', required: false },
        { name: 'pinAcknowledge', required: false },
      ],
      LIVE_CHAT: [
        { name: 'websiteDomin', required: true },
        { name: 'widgetColor', required: false },
        { name: 'welcomeTitle', required: true },
        { name: 'welcomeTagline', required: true },
      ],
      SMS: [
        { name: 'accountSid', required: true },
        { name: 'authToken', required: true },
        { name: 'srcNumber', required: true },
      ],
      LINE: [
        { name: 'lineChannelSecret', required: true },
        { name: 'lineChannelId', required: true },
      ],
      TELEGRAM: [{ name: 'telegramBotToken', required: true }],
      VOICE: [{ name: 'srcNumber', required: false }],
      MICROSOFTTEAMS: [],
      MESSENGER: [],
      INSTAGRAM: [],
    };

    const channelFields = fieldMap[selectedChannel?.channelType] || [];

    // WHAT & WHY: Check selectedChannel.config.settings.provider as well as EmailConfig
    // because backend edit API returns provider under config.settings.provider.
    const emailProvider =
      selectedChannel?.config?.settings?.provider ??
      selectedChannel?.EmailConfig?.[0]?.provider;

    if (
      selectedChannel?.channelType === 'EMAIL' &&
      (emailProvider === 'other' || emailProvider === 'other_provider')
    ) {
      channelFields.push(
        { name: 'smtpEnabled', required: false },
        { name: 'smtpAddress', required: true },
        { name: 'smtpPort', required: true },
        { name: 'smtpLogin', required: true },
        { name: 'smtpPassword', required: true },
        { name: 'smtpDomain', required: true },
        { name: 'imapEnabled', required: false },
        { name: 'imapAddress', required: true },
        { name: 'imapPort', required: true },
        { name: 'imapLogin', required: true },
        { name: 'imapPassword', required: true },
        { name: 'imapEnableSsl', required: false }
      );
    }

    return [...base, ...channelFields];
  }, [selectedChannel]);

  const ChennalEditSchema = validationSchema(fldList, t).superRefine((data, ctx) => {
    const formData = data as SmtpSettings & ImapSettings & EditChannelSchemaType;

    validateRequired(formData.channelName, ctx, 'channelName', 'Channel name is required');

    // WHAT & WHY: Validate WhatsApp PIN whenever Facebook is reconnected OR when user fills in a PIN.
    if (selectedChannel?.channelType === 'WHATSAPP' && (isFacebookConnected || formData.pin)) {
      const trimmedPin = formData.pin?.trim() ?? '';
      if (!trimmedPin) {
        addRequiredError(ctx, 'pin', '2FA PIN is required');
      } else if (!/^\d{6}$/.test(trimmedPin)) {
        addRequiredError(ctx, 'pin', t('PIN_NUMERIC_ONLY'));
      }
      if (formData.pinAcknowledge !== true) {
        addRequiredError(ctx, 'pinAcknowledge', 'Please acknowledge the warnings');
      }
    }

    if (selectedChannel?.channelType === 'EMAIL') {
      validateRequired(formData.email, ctx, 'email', 'Email is required');
    }

    if (selectedChannel?.channelType === 'LIVE_CHAT') {
      validateRequired(formData.websiteDomin, ctx, 'websiteDomin', 'Website domain is required');
      validateRequired(
        formData.welcomeTagline,
        ctx,
        'welcomeTagline',
        'Welcome tagline is required'
      );
      validateRequired(formData.welcomeTitle, ctx, 'welcomeTitle', 'Welcome title is required');
    }

    if (formData.smtpEnabled) {
      validateRequired(formData.smtpAddress, ctx, 'smtpAddress', 'Address is required');
      validatePort(Number(formData.smtpPort), ctx, 'smtpPort');
      validateRequired(formData.smtpLogin, ctx, 'smtpLogin', 'Login is required');
      validateRequired(formData.smtpPassword, ctx, 'smtpPassword', 'Password is required');
      validateRequired(formData.smtpDomain, ctx, 'smtpDomain', 'Domain is required');
    }

    if (formData.imapEnabled) {
      validateRequired(formData.imapAddress, ctx, 'imapAddress', 'Address is required');
      validatePort(formData.imapPort, ctx, 'imapPort');
      validateRequired(formData.imapLogin, ctx, 'imapLogin', 'Login is required');
      validateRequired(formData.imapPassword, ctx, 'imapPassword', 'Password is required');
    }

    if (selectedChannel?.channelType === 'SMS') {
      validateRequired(formData.accountSid, ctx, 'accountSid', 'AccountSid is required');
      validateRequired(formData.authToken, ctx, 'authToken', 'AuthToken is required');
      validateRequired(formData.srcNumber, ctx, 'srcNumber', 'Source number is required');
    }

    if (selectedChannel?.channelType === 'LINE') {
      validateRequired(
        formData.lineChannelSecret,
        ctx,
        'lineChannelSecret',
        'Line channel secret is required'
      );
      validateRequired(formData.lineChannelId, ctx, 'lineChannelId', 'Line channel ID is required');
    }

    if (selectedChannel?.channelType === 'TELEGRAM') {
      validateRequired(
        formData.telegramBotToken,
        ctx,
        'telegramBotToken',
        'Telegram bot token is required'
      );
    }
  });

  return { validationSchema: ChennalEditSchema };
};
